// Rozhovor s AI — admin obhajuje tip (trh), AI konfrontuje daty → op-ed článek.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChatsCircle, PaperPlaneRight, Article, Robot, User, ClockCounterClockwise } from "@phosphor-icons/react";
import { timingSearchPlayers } from "../../lib/timing-api";
import { interviewStart, interviewReply, interviewFinish, interviewList } from "../../lib/blog-api";

export default function BlogInterview({ onArticleCreated }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [player, setPlayer] = useState(null);
    const [tip, setTip] = useState("");
    const [interview, setInterview] = useState(null);
    const [reply, setReply] = useState("");
    const [busy, setBusy] = useState(false);
    const [archive, setArchive] = useState(null);

    const loadArchive = async () => {
        if (archive) { setArchive(null); return; }
        try { const d = await interviewList(); setArchive(d.items || []); }
        catch { toast.error("Načtení archivu selhalo"); }
    };

    const openFromArchive = (it) => {
        setInterview({ id: it.id, player_name: it.player_name, messages: it.messages, status: it.status });
        setArchive(null);
    };

    useEffect(() => {
        if (q.trim().length < 2 || player) { setResults([]); return; }
        const t = setTimeout(() => {
            timingSearchPlayers({ q, limit: 6 }).then((d) => setResults(d.players || [])).catch(() => {});
        }, 300);
        return () => clearTimeout(t);
    }, [q, player]);

    const start = async () => {
        if (!player || tip.trim().length < 10) { toast.error("Vyber hráče a napiš svůj tip (aspoň větu)"); return; }
        setBusy(true);
        try {
            const d = await interviewStart({ player_key: player.player_key, tip });
            setInterview(d);
        } catch (e) { toast.error(e.response?.data?.detail || "Start rozhovoru selhal"); }
        finally { setBusy(false); }
    };

    const send = async () => {
        if (reply.trim().length < 3) return;
        setBusy(true);
        try {
            const d = await interviewReply(interview.id, reply);
            setInterview(d); setReply("");
        } catch (e) { toast.error(e.response?.data?.detail || "Odpověď selhala"); }
        finally { setBusy(false); }
    };

    const finish = async () => {
        setBusy(true);
        try {
            await interviewFinish(interview.id);
            toast.success("Generuji op-ed článek z debaty — objeví se v seznamu jako draft (~60 s)");
            setInterview(null); setPlayer(null); setTip(""); setQ("");
            onArticleCreated?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Generování selhalo"); }
        finally { setBusy(false); }
    };

    return (
        <div className="border border-fa-violet/40 bg-black/50 p-4 space-y-3" data-testid="blog-interview-panel">
            <div className="font-mono text-[9px] uppercase tracking-widest text-fa-cream flex items-center gap-1.5">
                <ChatsCircle size={12} /> Rozhovor s AI — obhaj svůj tip, AI tě konfrontuje daty
                <button type="button" onClick={loadArchive} data-testid="interview-archive-btn"
                        className="ml-auto flex items-center gap-1 text-white/45 hover:text-fa-cream normal-case">
                    <ClockCounterClockwise size={11} /> Archiv debat
                </button>
            </div>

            {archive && (
                <div className="border border-white/15 bg-black/40 max-h-48 overflow-y-auto" data-testid="interview-archive">
                    {archive.length === 0 && <p className="px-3 py-2 font-mono text-[10px] text-white/40">Žádné debaty.</p>}
                    {archive.map((it) => (
                        <button key={it.id} type="button" onClick={() => openFromArchive(it)} data-testid={`interview-archive-${it.id}`}
                                className="w-full text-left px-3 py-2 font-mono text-[10px] hover:bg-white/5 flex items-center gap-2 border-b border-white/5">
                            <span className="font-bold">{it.player_name}</span>
                            <span className="text-white/40">{it.rounds} kol · {(it.created_at || "").slice(0, 10)}</span>
                            <span className={`ml-auto text-[8px] uppercase px-1.5 py-0.5 border ${it.post_id ? "border-fa-mint/50 text-fa-mint" : "border-white/20 text-white/50"}`}>
                                {it.post_id ? "má článek" : it.status === "open" ? "otevřená" : "uzavřená"}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {!interview && (<>
                <div className="relative max-w-md">
                    <input value={player ? player.display_name : q}
                           onChange={(e) => { setPlayer(null); setQ(e.target.value); }}
                           placeholder="Hráč, o kterém se povede debata…"
                           className="w-full bg-black/40 border border-white/25 px-3 py-2 font-mono text-[12px] text-white focus:outline-none focus:border-fa-cream placeholder:text-white/35"
                           data-testid="interview-player-search" />
                    {results.length > 0 && !player && (
                        <div className="absolute z-30 left-0 right-0 top-full bg-[#111] border border-white/15 max-h-48 overflow-y-auto">
                            {results.map((p) => (
                                <button key={p.player_key} type="button" onClick={() => { setPlayer(p); setResults([]); }}
                                        data-testid={`interview-result-${p.player_key}`}
                                        className="w-full text-left px-3 py-1.5 font-mono text-[11px] hover:bg-white/5">
                                    <span className="font-bold">{p.display_name}</span>
                                    <span className="text-white/45 ml-2">{(p.positions || []).join("/")}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <textarea value={tip} onChange={(e) => setTip(e.target.value)} rows={3}
                          placeholder="Tvůj tip / teze (pohled trhu), kterou budeš obhajovat…"
                          className="w-full bg-black/40 border border-white/25 px-3 py-2 font-mono text-[12px] text-white focus:outline-none focus:border-fa-cream placeholder:text-white/35"
                          data-testid="interview-tip" />
                <button type="button" onClick={start} disabled={busy} data-testid="interview-start"
                        className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-cream text-fa-cream hover:bg-fa-cream hover:text-black font-bold disabled:opacity-40">
                    {busy ? "AI načítá data…" : "Zahájit konfrontaci"}
                </button>
            </>)}

            {interview && (<>
                <div className="font-mono text-[10px] text-white/50">Debata: <span className="text-white font-bold">{interview.player_name}</span> · {Math.floor(interview.messages.length / 2)} kol</div>
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="interview-messages">
                    {interview.messages.map((m, i) => (
                        <div key={i} className={`border p-2.5 ${m.role === "admin" ? "border-fa-yellow/30 bg-fa-yellow/5 ml-8" : "border-fa-violet/30 bg-fa-violet/5 mr-8"}`}>
                            <div className="font-mono text-[8px] uppercase tracking-widest mb-1 flex items-center gap-1">
                                {m.role === "admin"
                                    ? <><User size={10} className="text-fa-yellow" /><span className="text-fa-yellow">Ty (trh)</span></>
                                    : <><Robot size={10} className="text-fa-cream" /><span className="text-fa-cream">AI analytik</span></>}
                            </div>
                            <p className="font-mono text-[11px] text-white/85 whitespace-pre-wrap">{m.text}</p>
                        </div>
                    ))}
                    {busy && <p className="font-mono text-[10px] text-white/40 animate-pulse">AI přemýšlí nad daty…</p>}
                </div>
                <div className="flex gap-2">
                    <input value={reply} onChange={(e) => setReply(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && !busy && send()}
                           placeholder="Tvoje obhajoba / protiargument…"
                           className="flex-1 bg-black/40 border border-white/25 px-3 py-2 font-mono text-[12px] text-white focus:outline-none focus:border-fa-yellow placeholder:text-white/35"
                           data-testid="interview-reply-input" />
                    <button type="button" onClick={send} disabled={busy} data-testid="interview-send"
                            className="font-mono text-[10px] px-3 border border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow hover:text-black disabled:opacity-40">
                        <PaperPlaneRight size={13} />
                    </button>
                </div>
                <button type="button" onClick={finish} disabled={busy || interview.messages.length < 4} data-testid="interview-finish"
                        className="w-full font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black font-bold flex items-center justify-center gap-1.5 disabled:opacity-40">
                    <Article size={12} /> Ukončit a vygenerovat op-ed článek (s anketou)
                </button>
                {interview.messages.length < 4 && <p className="font-mono text-[8px] text-white/35 text-center">Odpověz aspoň jednou, ať má článek co konfrontovat</p>}
            </>)}
        </div>
    );
}
